import { getApolloAuthClient } from '@faustwp/core';
import { useRouter } from 'next/router';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { GetBlogQuery } from '../../../__generated__/graphql';
import Input from '../../../components/Input';
import Select, { SelectOption } from '../../../components/Select';
import { useHeaderDispatch } from '../../../layout/Header/state/hooks';
import BlogQuery from '../../../pages/about-us/blog/blogQuery.gql';
import { setSessionStorage } from '../../../utils/sessionStorage';
import { useDebounceInput } from '../../../utils/useDebounceInput';
import { useRouterParams } from '../../../utils/useRouterParams';

import style from './filter-bar.module.css';

export const searchId = 'blog-filter-search';

const FilterBar: React.FC = () => {
  const router = useRouter();

  const headerDispatch = useHeaderDispatch();

  const blogQuery = getApolloAuthClient().readQuery<GetBlogQuery>({
    query: BlogQuery,
  });

  const routerParams = useRouterParams();

  const [debouncedSearchValue, setDebouncedSearchValue] = useDebounceInput(routerParams.search ?? '');
  const [localSearchValue, setLocalSearchValue] = useState(debouncedSearchValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setLocalSearchValue(value);
    setDebouncedSearchValue(value);
  };

  const updateRouterParams = useCallback((newParams: Record<string, string>) => {
    void router.replace({
      pathname: router.pathname,
      query: {
        cursors: '[]',
        ...newParams,
      },
    }, undefined, {
      scroll: false,
    });
  }, [router]);

  const handleFocusSearch = () => {
    if (routerParams.focusSearch !== 'true') {
      updateRouterParams({
        ...routerParams,
        focusSearch: 'true',
      });
    }
  };

  const handleBlurSearch = () => {
    if (routerParams.focusSearch !== 'false') {
      updateRouterParams({
        ...routerParams,
        focusSearch: 'false',
      });
    }
  };

  useEffect(() => {
    if ((routerParams.search ?? '') !== debouncedSearchValue) {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        search: currentSearch,
        ...query
      } = routerParams;

      updateRouterParams({
        ...query,
        ...debouncedSearchValue === '' ? {} : { search: debouncedSearchValue },
      });
    }
  }, [debouncedSearchValue, routerParams, updateRouterParams]);

  useEffect(() => {
    if (routerParams.focusSearch === 'true') {
      inputRef.current?.focus();
    }
  }, [routerParams.focusSearch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(router.query as Record<string, string>);

    const queryString = urlParams.toString();

    setSessionStorage('blogIndexQueryParams', queryString);

    headerDispatch({
      payload: {
        blogIndexQueryString: queryString,
      },
      type: 'updateBlogIndexQueryString',
    });
  }, [headerDispatch, router.query]);

  if (!blogQuery) {
    return null;
  }

  const handleSetCategory = (event: FormEvent<HTMLSelectElement>) => {
    const categoryId = event.currentTarget.value;

    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      categoryId: currentCategoryId,
      ...newParams
    } = routerParams;

    updateRouterParams({
      ...newParams,
      ...categoryId === '' ? {} : { categoryId },
    });
  };

  const categoryOptions: SelectOption[] = [
    {
      label: 'All Segments',
      value: '',
    },
    ...(blogQuery.categories?.nodes ?? []).reduce<SelectOption[]>((acc, category): SelectOption[] => {
      if (category.count != null) {
        const categoryId = category.databaseId.toString();

        return [
          ...acc,
          {
            label: category.name ?? '',
            value: categoryId,
          }];
      }

      return acc;
    }, [])];

  const handleSetTag = (event: FormEvent<HTMLSelectElement>) => {
    const tagId = event.currentTarget.value;

    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tagId: currentTagId,
      ...newParams
    } = routerParams;

    updateRouterParams({
      ...newParams,
      ...tagId === '' ? {} : { tagId },
    });
  };

  const tagOptions: SelectOption[] = [
    {
      label: 'All Tags',
      value: '',
    },
    ...(blogQuery.tags?.nodes ?? []).map((tag): SelectOption => ({
      label: tag.name ?? '',
      value: tag.databaseId.toString(),
    })),
  ];

  return (
    // these divs need to be separate, see Section component
    <div className={style.filterBar}>
      <div className={style.filterBarContainer}>
        <div className={style.filterBarInputContainer}>
          <Select
            label="Segment"
            labelClassName={style.filterBarInputLabel}
            onChange={handleSetCategory}
            options={categoryOptions}
            selectClassName={style.filterBarInput}
            value={routerParams.categoryId ?? ''}
          />

          <Select
            label="Tag"
            labelClassName={style.filterBarInputLabel}
            onChange={handleSetTag}
            options={tagOptions}
            selectClassName={style.filterBarInput}
            value={routerParams.tagId ?? ''}
          />

          <Input
            containerClassName={style.filterBarInputLabel}
            inputClassName={style.filterBarInput}
            inputMode="search"
            label="Search"
            labelId={searchId}
            onBlur={handleBlurSearch}
            onChange={handleSearchInputChange}
            onFocus={handleFocusSearch}
            placeholder="Search"
            ref={inputRef}
            type="search"
            value={localSearchValue}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
